/**
 * Utility functions for booking-related operations and formatting
 */

/**
 * Format duration in minutes to human readable string
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration string
 */
export const formatDuration = (minutes, { compact = false } = {}) => {
  if (minutes < 60) {
    return compact ? `${minutes}m` : `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0
    ? `${hours}h ${remainingMinutes}m`
    : `${hours}h`;
};

/**
 * Format date and time for display
 * @param {string} selectedDate - Date string
 * @param {string} selectedTime - Time string in HH:MM format
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (selectedDate, selectedTime) => {
  if (!selectedDate || !selectedTime) return 'Not selected';
  
  const date = new Date(selectedDate);
  const time = selectedTime;
  
  const dateStr = date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  // Convert 24-hour time to 12-hour format with AM/PM
  const formatTime = (timeStr) => {
    if (!timeStr) return timeStr;
    
    // Handle different time formats that might come in
    const timeParts = timeStr.split(':');
    if (timeParts.length < 2) return timeStr;
    
    let hours = parseInt(timeParts[0], 10);
    const minutes = timeParts[1];
    
    if (isNaN(hours)) return timeStr;
    
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    
    return `${hours}:${minutes} ${ampm}`;
  };
  
  const formattedTime = formatTime(time);
  return `${dateStr} at ${formattedTime}`;
};

/**
 * Get location display text based on appointment type and selected location
 * @param {string} selectedAppointmentType - Type of appointment ('mobile', 'fixed', etc.)
 * @param {Object} selectedAddress - Selected mobile address object
 * @param {Object} selectedLocation - Selected fixed location object
 * @param {Object} fixedLocationData - Fixed location data with all available locations
 * @returns {string} Location display text
 */
export const getLocationDisplay = (selectedAppointmentType, selectedAddress, selectedLocation, fixedLocationData) => {
  if (selectedAppointmentType === 'mobile') {
    // For mobile appointments, show the selected address
    // Primary field used in AddressInputStep is 'name'
    if (selectedAddress?.name) {
      return selectedAddress.name;
    } else if (selectedAddress?.full) {
      return selectedAddress.full;
    } else if (selectedAddress?.address) {
      return selectedAddress.address;
    }
    return 'Mobile address not selected';
  } else if (selectedAppointmentType === 'fixed') {
    // For fixed appointments, show the selected location or default location.
    // When the address is hidden (null), show the name + the confirmation note.
    if (fixedLocationData?.locations?.length > 1) {
      // Multiple locations available, show selected one
      if (selectedLocation?.address) {
        return selectedLocation.address;
      } else if (selectedLocation?.full) {
        return selectedLocation.full;
      } else if (selectedLocation?.name) {
        // Address is hidden — show name + note
        return `${selectedLocation.name} — Exact address shared once your booking is confirmed.`;
      }
      return 'Fixed location not selected';
    } else if (fixedLocationData?.locations?.length === 1) {
      // Only one location available, show it
      const location = fixedLocationData.locations[0];
      if (location.address || location.full) {
        return location.address || location.full;
      } else if (location.name) {
        // Address is hidden — show name + note
        return `${location.name} — Exact address shared once your booking is confirmed.`;
      }
      return 'Fixed location';
    }
    return 'Fixed location';
  }
  return 'Location not selected';
};